import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Smartphone } from "lucide-react";

/**
 * Install banner for both PWAs.
 * - On Chrome/Edge/Android: uses beforeinstallprompt event
 * - On iOS Safari: shows a "Add to Home Screen" hint
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const loc = useLocation();

  const isStaff = loc.pathname.startsWith("/admin");
  const storageKey = isStaff ? "pwa-staff-dismissed" : "pwa-customer-dismissed";

  useEffect(() => {
    if (localStorage.getItem(storageKey)) return;
    // Already installed?
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Android/Chrome path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari path (no prompt event) — show hint after brief delay
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIos && isSafari) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 6000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [storageKey]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") localStorage.setItem(storageKey, "1");
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(storageKey, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60]">
      <div className={`shadow-2xl border p-4 flex items-start gap-3 ${isStaff ? "bg-[#0F172A] text-[#FFFFFF] border-[#D4AF37]/40" : "bg-white text-[#0F172A] border-[#E2E8F0]"}`}>
        <div className={`p-2 ${isStaff ? "bg-[#D4AF37] text-[#0F172A]" : "bg-[#0F172A] text-[#FFFFFF]"}`}>
          <Smartphone size={18}/>
        </div>
        <div className="flex-1">
          <div className="font-serif-display text-xl leading-tight">
            {isStaff ? "Install the Staff app" : "Install Hedieh's on your phone"}
          </div>
          {iosHint ? (
            <p className="text-xs opacity-80 mt-1">
              Tap <strong>Share</strong> then <strong>“Add to Home Screen”</strong> to install.
            </p>
          ) : (
            <p className="text-xs opacity-80 mt-1">
              {isStaff
                ? "Bookings, promos and content — one tap from your home screen."
                : "Book fittings and track orders offline-ready."}
            </p>
          )}
          {!iosHint && (
            <button data-testid="pwa-install-btn" onClick={install}
                    className={`mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-4 py-2 ${isStaff ? "bg-[#D4AF37] text-[#0F172A]" : "bg-[#0F172A] text-[#FFFFFF]"}`}>
              <Download size={12}/> Install
            </button>
          )}
        </div>
        <button data-testid="pwa-install-dismiss" onClick={dismiss} className="opacity-60 hover:opacity-100">
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
